import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MobileNav from "@/components/MobileNav";
import { Search, ArrowLeft, Plus, Check, Image, X, Camera, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ColorsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [board, setBoard] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [colorDesigns, setColorDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);

  const brands = ["OPI", "Essie", "Sally Hansen"];
  const families = ["Red", "Pink", "White", "Black", "Purple", "Grey", "Nude"];

  useEffect(() => {
    fetchColors();
    fetchBoard();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchColors(), 300);
    return () => clearTimeout(timer);
  }, [search, brandFilter, familyFilter]);

  const fetchColors = async () => {
    try {
      const params = new URLSearchParams();
      if (brandFilter !== "all") params.append("brand", brandFilter);
      if (familyFilter !== "all") params.append("color_family", familyFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/colors?${params}`, { withCredentials: true });
      setColors(response.data);
    } catch (error) {
      console.error("Error fetching colors:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoard = async () => {
    try {
      const response = await axios.get(`${API}/board/current`, { withCredentials: true });
      setBoard(response.data);
    } catch (error) {
      console.error("Error fetching board:", error);
    }
  };

  const openColorDetail = async (color) => {
    setSelectedColor(color);
    setLoadingDesigns(true);
    setColorDesigns([]);
    
    try {
      const response = await axios.get(`${API}/colors/${color.color_id}/designs`, { withCredentials: true });
      setColorDesigns(response.data || []);
    } catch (error) {
      console.error("Error fetching designs:", error);
    } finally {
      setLoadingDesigns(false);
    }
  };

  const closeColorDetail = () => {
    setSelectedColor(null);
    setColorDesigns([]);
  };

  const addColorToBoard = async (color) => {
    if (!board) {
      toast.error("Please check in first");
      return;
    }

    if (board.status !== "editing") {
      toast.error("Your appointment is locked");
      return;
    }

    const isSelected = board.selected_colors?.some(c => c.color_id === color.color_id);

    try {
      if (isSelected) {
        await axios.post(
          `${API}/board/${board.board_id}/remove-color?color_id=${color.color_id}`,
          {},
          { withCredentials: true }
        );
        setBoard(prev => ({
          ...prev,
          selected_colors: prev.selected_colors.filter(c => c.color_id !== color.color_id)
        }));
        toast.success("Color removed");
      } else {
        await axios.post(
          `${API}/board/${board.board_id}/add-color?color_id=${color.color_id}`,
          {},
          { withCredentials: true }
        );
        setBoard(prev => ({
          ...prev,
          selected_colors: [...(prev.selected_colors || []), {
            color_id: color.color_id,
            name: color.name,
            brand: color.brand,
            hex_code: color.hex_code,
            product_code: color.product_code
          }]
        }));
        toast.success("Color added to your appointment!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update selection");
    }
  };

  const addDesignToBoard = async (design) => {
    if (!board) {
      toast.error("Please check in first");
      return;
    }

    if (board.status !== "editing") {
      toast.error("Your appointment is locked");
      return;
    }

    const isSelected = board.selected_designs?.some(d => d.portfolio_id === design.portfolio_id);

    try {
      if (isSelected) {
        await axios.post(
          `${API}/board/${board.board_id}/remove-design?portfolio_id=${design.portfolio_id}`,
          {},
          { withCredentials: true }
        );
        setBoard(prev => ({
          ...prev,
          selected_designs: prev.selected_designs.filter(d => d.portfolio_id !== design.portfolio_id)
        }));
        toast.success("Design removed");
      } else {
        await axios.post(
          `${API}/board/${board.board_id}/add-design?portfolio_id=${design.portfolio_id}`,
          {},
          { withCredentials: true }
        );
        setBoard(prev => ({
          ...prev,
          selected_designs: [...(prev.selected_designs || []), {
            portfolio_id: design.portfolio_id,
            title: design.title,
            image_url: design.image_url
          }]
        }));
        toast.success("Design added to your appointment!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update selection");
    }
  };

  const isColorSelected = (colorId) => {
    return board?.selected_colors?.some(c => c.color_id === colorId);
  };

  const isDesignSelected = (portfolioId) => {
    return board?.selected_designs?.some(d => d.portfolio_id === portfolioId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32" data-testid="colors-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/board" className="text-gray-500 hover:text-black">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="font-display text-xl font-bold">Colors</h1>
            </div>
            {board && (
              <Badge variant="outline">
                {board.selected_colors?.length || 0} selected
              </Badge>
            )}
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or product code..."
              className="pl-10 rounded-xl border-gray-200"
              data-testid="color-search"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-2">
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-[120px] rounded-xl">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={familyFilter} onValueChange={setFamilyFilter}>
              <SelectTrigger className="w-[120px] rounded-xl">
                <SelectValue placeholder="Color" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Colors</SelectItem>
                {families.map(f => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {/* Color Grid - Glossy nail swatch like salon books */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
          {colors.map((color) => (
            <button
              key={color.color_id}
              onClick={() => openColorDetail(color)}
              className={`relative group transition-all ${
                isColorSelected(color.color_id) ? "ring-2 ring-black ring-offset-2 rounded-2xl" : ""
              }`}
              data-testid={`color-${color.color_id}`}
            >
              {/* Color Swatch Container */}
              <div className="relative rounded-2xl overflow-hidden group-hover:shadow-xl transition-all bg-gray-50">
                
                {/* Normal Color Swatch (visible by default) */}
                <div 
                  className="aspect-square relative"
                  style={{ backgroundColor: color.hex_code }}
                >
                  {/* Glossy finish overlay - like real salon books */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/10 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
                  {/* Shine highlight */}
                  <div className="absolute top-3 left-3 w-6 h-3 bg-white/50 rounded-full blur-[2px] transform -rotate-12" />
                </div>
                
                {/* Hover State - Show as glossy nail tip + design preview */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white flex flex-col">
                  {/* Glossy Nail Shape */}
                  <div className="flex-1 flex items-center justify-center p-2">
                    <div 
                      className="w-14 h-16 rounded-t-full relative"
                      style={{ backgroundColor: color.hex_code }}
                    >
                      {/* Glossy shine effect */}
                      <div className="absolute inset-0 rounded-t-full bg-gradient-to-br from-white/50 via-white/20 to-transparent" />
                      <div className="absolute top-2 left-2 w-4 h-2 bg-white/60 rounded-full blur-[1px]" />
                      {/* Cuticle line */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-t from-black/10 to-transparent" />
                    </div>
                  </div>
                  
                  {/* Quick design count badge */}
                  <div className="text-center pb-2">
                    <span className="text-[10px] text-gray-600">Tap to see designs</span>
                  </div>
                </div>
                
                {/* Selection checkmark */}
                {isColorSelected(color.color_id) && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-black rounded-full flex items-center justify-center shadow-md z-10">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
              </div>
              
              {/* Color Info */}
              <div className="mt-2 text-center px-1">
                <p className="text-xs font-medium text-gray-900 truncate">{color.name}</p>
                <p className="text-[10px] text-gray-600">{color.brand}</p>
              </div>
            </button>
          ))}
        </div>

        {colors.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No colors found</p>
          </div>
        )}
      </main>

      {/* Color Detail Popup */}
      <Dialog open={!!selectedColor} onOpenChange={() => closeColorDetail()}>
        <DialogContent className="max-w-md mx-auto p-0 rounded-3xl overflow-hidden">
          {selectedColor && (
            <>
              {/* Color Header */}
              <div 
                className="h-32 relative"
                style={{ backgroundColor: selectedColor.hex_code }}
              >
                <button
                  onClick={closeColorDetail}
                  className="absolute top-3 right-3 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="p-6">
                {/* Color Info */}
                <div className="mb-6">
                  <h2 className="font-display text-2xl font-bold mb-1">{selectedColor.name}</h2>
                  <p className="text-gray-600">{selectedColor.brand}</p>
                  <p className="font-mono text-sm text-gray-500">{selectedColor.product_code}</p>
                </div>
                
                {/* Add to Board Button */}
                <Button
                  onClick={() => addColorToBoard(selectedColor)}
                  className={`w-full mb-6 rounded-xl h-12 ${
                    isColorSelected(selectedColor.color_id)
                      ? "bg-gray-100 hover:bg-gray-200 text-gray-900"
                      : "bg-black hover:bg-gray-900 text-white"
                  }`}
                  disabled={!board}
                >
                  {isColorSelected(selectedColor.color_id) ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Selected
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add to My Board
                    </>
                  )}
                </Button>
                
                {/* Designs Using This Color */}
                <div>
                  <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4 text-gray-500" />
                    Designs with this color
                  </h3>
                  
                  {loadingDesigns ? (
                    <div className="flex justify-center py-8">
                      <div className="w-6 h-6 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
                    </div>
                  ) : colorDesigns.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {colorDesigns.map((design) => (
                        <button
                          key={design.portfolio_id}
                          onClick={() => addDesignToBoard(design)}
                          className={`relative group text-left ${
                            isDesignSelected(design.portfolio_id) ? "ring-2 ring-black ring-offset-1" : ""
                          }`}
                        >
                          <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                            <img
                              src={design.image_url}
                              alt={design.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              onError={(e) => { e.target.src = "https://via.placeholder.com/200?text=Design"; }}
                            />
                          </div>
                          <p className="text-xs font-medium mt-1 truncate">{design.title}</p>
                          {isDesignSelected(design.portfolio_id) && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-black rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-600">No designs found with this color</p>
                      <Link to="/portfolio" className="text-sm text-black underline mt-1 inline-block">
                        Browse all designs
                      </Link>
                    </div>
                  )}
                </div>
                
                {/* Upload Your Own */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <Link to="/inspirations" className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Camera className="w-5 h-5 text-gray-500" />
                      <span className="text-sm font-medium">Upload your own inspiration</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <MobileNav />
    </div>
  );
};

export default ColorsPage;
